import { toast } from "react-toastify"

import { REQ } from "constants/api/requests"
import { IResponsePayload } from "constants/interfaces"

import { Paginated } from "services/tags/type"

import useApi from "util/api"

import {
  BalanceMethodsResponse,
  BalanceProcessorsResponse,
  BalanceTerminalsResponse,
  BalanceTxType,
  CreatePaymentRouteInput,
  PaymentRoute,
  PspBalance,
  PspBalanceLedgerResponse,
  PspBalancesResponse
} from "./type"

class BalanceService {
  async getTerminalProcessorsTable(
    page: number,
    pageSize: number,
    _id: number | undefined,
    filters: { tx_type: BalanceTxType; start?: string; end?: string } | undefined,
    sortRow?: string,
    sortOrder?: string
  ): Promise<BalanceProcessorsResponse | void> {
    // SOFT-559: do NOT swallow — a 503 (financial-service down on an FS-era window) must reach the
    // table so it can show a retry affordance and keep the already-rendered rows, instead of a
    // generic toast + a blanked table. The AccordionTable handles the error.
    const params = { page, pageSize, sortRow, sortOrder, ...(filters ?? {}) }
    const { data } = await useApi.get<IResponsePayload<BalanceProcessorsResponse>>(REQ.ADMIN_DASHBOARD.BALANCE.PROCESSORS, { params })
    if (data?.success) return data.response.payload
  }

  async getTerminalMethodsTable(
    page: number,
    pageSize: number,
    processor: string,
    filters: { tx_type: BalanceTxType; start?: string; end?: string } | undefined,
    sortRow?: string,
    sortOrder?: string
  ): Promise<BalanceMethodsResponse | void> {
    // SOFT-559: propagate errors (see getTerminalProcessorsTable) — the AccordionTable renders the
    // 503/retry state and keeps the current rows.
    const params = { page, pageSize, sortRow, sortOrder, ...(filters ?? {}) }
    const url = REQ.ADMIN_DASHBOARD.BALANCE.METHODS.replace(":processor", String(processor))
    const { data } = await useApi.get<IResponsePayload<BalanceMethodsResponse>>(url, { params })
    if (data?.success) return data.response.payload
  }

  async getTerminalTerminalsTable(
    processor: string,
    payment_method: string,
    params: {
      tx_type: BalanceTxType
      start?: string
      end?: string
      page: number
      pageSize: number
      sortRow?: string
      sortOrder?: string
    }
  ): Promise<BalanceTerminalsResponse | void> {
    // SOFT-559: propagate — the editor table shows the 503/retry state and keeps its current rows.
    const url = REQ.ADMIN_DASHBOARD.BALANCE.TERMINALS.replace(":processor", String(processor)).replace(
      ":payment_method",
      String(payment_method)
    )
    const { data } = await useApi.get<IResponsePayload<BalanceTerminalsResponse>>(url, { params })
    if (data?.success) return data.response.payload
  }

  async upsertTerminalFee(payload: {
    tx_type: "DEPOSIT" | "WITHDRAWAL"
    route_id: number
    percent_bps: number
    flat_usd_cents: number
    rolling_reserve_bps: number
    settlement_fee_bps: number
  }) {
    try {
      const { data } = await useApi.put<IResponsePayload<any>>(REQ.ADMIN_DASHBOARD.BALANCE.TERMINAL_FEE, payload)
      if (data?.success) return data.response.payload
    } catch (e: any) {
      toast.error(e?.data?.message ?? e?.message ?? "Failed to update fee")
    }
  }

  async listPaymentRoutes(params?: {
    processor?: string
    payment_method?: string
    terminal_name?: string
    is_active?: "true" | "false"
    source?: "AUTO" | "MANUAL"
    page?: number
    pageSize?: number
    sortRow?: string
    sortOrder?: "asc" | "desc"
  }): Promise<Paginated<PaymentRoute> | void> {
    try {
      const { data } = await useApi.get<IResponsePayload<Paginated<PaymentRoute>>>(REQ.ADMIN_DASHBOARD.BALANCE.ROUTES, { params })
      if (data?.success) return data.response.payload
    } catch (e: any) {
      toast.error(e?.data?.message ?? e?.message ?? "Failed to fetch routes")
    }
  }

  async listPaymentRouteProcessors(): Promise<string[] | void> {
    try {
      const { data } = await useApi.get<IResponsePayload<string[]>>(`${REQ.ADMIN_DASHBOARD.BALANCE.ROUTES_PROCESSORS}`)
      if (data?.success) return data.response.payload
    } catch (e: any) {
      toast.error(e?.data?.message ?? e?.message ?? "Failed to fetch processors")
    }
  }

  async createPaymentRoute(payload: CreatePaymentRouteInput): Promise<PaymentRoute | void> {
    try {
      const { data } = await useApi.post<IResponsePayload<PaymentRoute>>(REQ.ADMIN_DASHBOARD.BALANCE.ROUTES, payload)
      if (data?.success) return data.response.payload
    } catch (e: any) {
      toast.error(e?.data?.message ?? e?.message ?? "Failed to create route")
    }
  }

  async exportFeesCsv(txType: BalanceTxType): Promise<boolean> {
    try {
      const { data } = await useApi.get<Blob>(REQ.ADMIN_DASHBOARD.BALANCE.FEES_EXPORT, {
        params: { tx_type: txType },
        responseType: "blob"
      })

      const blob = new Blob([data], { type: "text/csv;charset=utf-8;" })
      const url = window.URL.createObjectURL(blob)
      const link = document.createElement("a")
      link.href = url
      link.setAttribute("download", `balance-fees-${txType.toLowerCase()}.csv`)
      document.body.appendChild(link)
      link.click()
      document.body.removeChild(link)
      window.URL.revokeObjectURL(url)

      return true
    } catch (e: any) {
      toast.error(e?.data?.message ?? "Failed to export balance fees")
      return false
    }
  }

  async importFeesCsv(file: File): Promise<{ created: number; updated: number; errors: string[] } | void> {
    try {
      const formData = new FormData()
      formData.append("file", file)

      const { data } = await useApi.post<IResponsePayload<{ created: number; updated: number; errors: string[] }>>(
        REQ.ADMIN_DASHBOARD.BALANCE.FEES_IMPORT,
        formData,
        { headers: { "Content-Type": "multipart/form-data" } }
      )

      if (!data?.success) {
        toast.error("Failed to import balance fees")
        return
      }

      const { created, updated, errors } = data.response.payload
      toast.success(`Imported fees: ${created} created, ${updated} updated${errors.length ? `, ${errors.length} errors` : ""}`)
      if (errors.length) errors.slice(0, 5).forEach((e) => toast.warn(e))
      return data.response.payload
    } catch (e: any) {
      toast.error(e?.data?.message ?? "Failed to import balance fees")
    }
  }

  async updatePaymentRoute(id: number, payload: Partial<CreatePaymentRouteInput> & { is_active?: boolean }): Promise<PaymentRoute | void> {
    try {
      const { data } = await useApi.put<IResponsePayload<PaymentRoute>>(
        REQ.ADMIN_DASHBOARD.BALANCE.ROUTES_UPDATE.replace(":id", String(id)),
        payload
      )
      if (data?.success) return data.response.payload
    } catch (e: any) {
      toast.error(e?.data?.message ?? e?.message ?? "Failed to update route")
    }
  }

  async listPspBalances(params?: {
    page?: number
    pageSize?: number
    processor?: string
    payment_method?: string
  }): Promise<PspBalancesResponse | void> {
    try {
      const { data } = await useApi.get<IResponsePayload<PspBalancesResponse>>(REQ.ADMIN_DASHBOARD.BALANCE.PSP_BALANCES, { params })
      if (data?.success) return data.response.payload
    } catch (e: any) {
      toast.error(e?.data?.message ?? e?.message ?? "Failed to fetch PSP balances")
    }
  }

  async topUpPspBalance(payload: {
    processor: string
    payment_method: string
    amount_cents: number
    note?: string
  }): Promise<PspBalance | void> {
    try {
      const { data } = await useApi.post<IResponsePayload<PspBalance>>(REQ.ADMIN_DASHBOARD.BALANCE.PSP_BALANCES_TOP_UP, payload)
      if (data?.success) {
        toast.success(`Topped up ${(payload.amount_cents / 100).toFixed(2)} USD`)
        return data.response.payload
      }
    } catch (e: any) {
      toast.error(e?.data?.message ?? e?.message ?? "Failed to top up")
    }
  }

  async resetPspBalance(payload: { processor: string; payment_method: string }): Promise<PspBalance | void> {
    try {
      const { data } = await useApi.post<IResponsePayload<PspBalance>>(REQ.ADMIN_DASHBOARD.BALANCE.PSP_BALANCES_RESET, payload)
      if (data?.success) {
        toast.success("Balance reset to 0")
        return data.response.payload
      }
    } catch (e: any) {
      toast.error(e?.data?.message ?? e?.message ?? "Failed to reset balance")
    }
  }

  async getPspBalanceLedger(
    processor: string,
    payment_method: string,
    params?: { page?: number; pageSize?: number }
  ): Promise<PspBalanceLedgerResponse | void> {
    try {
      const url = REQ.ADMIN_DASHBOARD.BALANCE.PSP_BALANCES_LEDGER.replace(":processor", processor).replace(
        ":payment_method",
        payment_method
      )
      const { data } = await useApi.get<IResponsePayload<PspBalanceLedgerResponse>>(url, { params })
      if (data?.success) return data.response.payload
    } catch (e: any) {
      toast.error(e?.data?.message ?? e?.message ?? "Failed to fetch ledger")
    }
  }

  async exportTransactionsCsv(params: {
    tx_type: BalanceTxType
    processor?: string
    payment_method?: string
    start?: string
    end?: string
  }): Promise<boolean> {
    try {
      const { data } = await useApi.get<Blob>(REQ.ADMIN_DASHBOARD.BALANCE.TRANSACTIONS_EXPORT, {
        params,
        responseType: "blob"
      })

      const blob = new Blob([data], { type: "text/csv;charset=utf-8;" })
      const url = window.URL.createObjectURL(blob)
      const link = document.createElement("a")
      link.href = url
      link.setAttribute("download", `transactions-${params.tx_type.toLowerCase()}.csv`)
      document.body.appendChild(link)
      link.click()
      document.body.removeChild(link)
      window.URL.revokeObjectURL(url)

      return true
    } catch (e: any) {
      toast.error(e?.data?.message ?? "Failed to export transactions")
      return false
    }
  }

  async resetAvailable(payload: { processor?: string; payment_method?: string; tx_type: BalanceTxType }): Promise<boolean> {
    try {
      const { data } = await useApi.post<IResponsePayload<{ updated: number }>>(REQ.ADMIN_DASHBOARD.BALANCE.RESET_AVAILABLE, payload)
      if (data?.success) {
        toast.success(`Available balance reset (${data.response.payload.updated} routes updated)`)
        return true
      }
      return false
    } catch (e: any) {
      toast.error(e?.data?.message ?? "Failed to reset available balance")
      return false
    }
  }

  async downloadSettlementReport(params: {
    processor: string
    payment_method?: string
    start: string
    end: string
    format: "pdf" | "csv"
  }): Promise<boolean> {
    try {
      const { data } = await useApi.get<Blob>(REQ.ADMIN_DASHBOARD.BALANCE.SETTLEMENT_REPORT, {
        params,
        responseType: "blob"
      })

      const ext = params.format === "csv" ? "csv" : "pdf"
      const mimeType = params.format === "csv" ? "text/csv" : "application/pdf"
      const blob = new Blob([data], { type: mimeType })
      const url = window.URL.createObjectURL(blob)
      const link = document.createElement("a")
      link.href = url
      link.setAttribute("download", `settlement-${params.processor}-${params.start}.${ext}`)
      document.body.appendChild(link)
      link.click()
      document.body.removeChild(link)
      window.URL.revokeObjectURL(url)

      return true
    } catch (e: any) {
      toast.error(e?.data?.message ?? "Failed to download settlement report")
      return false
    }
  }
}

export const balanceService = new BalanceService()
